import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

interface Booking {
  _id: string;
  flightId: string;
  passengerName: string;
  passportNumber: string;
  status: string;
  createdAt: string;
}

const FlightBookingDashboard: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(10);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchBookings = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/bookings?status=Confirmed`,
        );
        if (isMounted) {
          setBookings(response.data);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) setError("Failed to fetch bookings");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchBookings();
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          fetchBookings();
          return 10;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (loading)
    return (
      <div style={{ textAlign: "center", marginTop: 40, fontSize: 20 }}>
        Loading...
      </div>
    );
  if (error)
    return (
      <div
        style={{
          textAlign: "center",
          marginTop: 40,
          color: "#d32f2f",
          fontSize: 18,
        }}
      >
        {error}
      </div>
    );

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "40px auto",
        padding: 24,
        background: "#f9f9fb",
        borderRadius: 16,
        boxShadow: "0 2px 12px #0001",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0, color: "#2d3a4a", fontWeight: 700 }}>
          Confirmed Flight Bookings
        </h2>
        <div
          style={{
            fontSize: 16,
            color: "#555",
            background: "#e3f2fd",
            borderRadius: 8,
            padding: "6px 16px",
            fontWeight: 500,
          }}
        >
          Refreshing in{" "}
          <span style={{ color: "#1976d2", fontWeight: 700 }}>{countdown}</span>{" "}
          sec
        </div>
      </div>
      {bookings.length === 0 ? (
        <div
          style={{
            color: "#888",
            textAlign: "center",
            padding: 32,
            fontSize: 18,
          }}
        >
          No confirmed bookings found.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#fff",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 1px 4px #0001",
            }}
          >
            <thead style={{ background: "#1976d2", color: "#fff" }}>
              <tr>
                <th style={{ padding: "12px 8px" }}>Booking ID</th>
                <th style={{ padding: "12px 8px" }}>Flight ID</th>
                <th style={{ padding: "12px 8px" }}>Passenger Name</th>
                <th style={{ padding: "12px 8px" }}>Passport Number</th>
                <th style={{ padding: "12px 8px" }}>Status</th>
                <th style={{ padding: "12px 8px" }}>Created At</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr
                  key={booking._id}
                  style={{ borderBottom: "1px solid #eee" }}
                >
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>
                    {booking._id}
                  </td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>
                    {booking.flightId}
                  </td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>
                    {booking.passengerName}
                  </td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>
                    {booking.passportNumber}
                  </td>
                  <td
                    style={{
                      padding: "10px 8px",
                      textAlign: "center",
                      color:
                        booking.status === "Confirmed" ? "#388e3c" : "#d32f2f",
                      fontWeight: 600,
                    }}
                  >
                    {booking.status}
                  </td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}>
                    {new Date(booking.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FlightBookingDashboard;
